/**
 * ObjectReferenceField React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import PropTypes from 'prop-types';
import connect from "react-redux/es/connect/connect";

// Material UI
import {withStyles} from "@material-ui/core";

// Material UI components
import InputLabel from '@material-ui/core/InputLabel';

// application components
import ObjectTable from "../listing/ObjectTable";
import ObjectTypeMenu from "../base/ObjectTypeMenu";

// application
import {pushAndEditObject, selectReference} from "../../operations/editing";
import {removeFieldElementAction, moveFieldElementAction} from "../../redux/editing/actions";
import {searchTag} from "../../operations/listing";
import {openMediaOverlayForObject} from "../../operations/view";
import {selectObjectsForIds} from "../../redux/editing/selectors";
import {selectDerivedTypes} from "../../redux/types/selectors";
import {parseBSON} from '../../utils';


/**
 * ObjectReferenceField is an editing component for object reference fields of objects
 *
 * @author Jens Gruschel
 */
export class ObjectReferenceField extends React.Component {

    objectTypeMenu = undefined;

    handleAddReference = (id, objectTypes, constraint, flags) => event => {
        if (objectTypes.length === 1) {
            const scope = this.props.scope || [];
            this.props.selectReference([...scope, id], objectTypes[0], constraint, flags);
        }
        else if (objectTypes.length > 1 && this.objectTypeMenu) {
            this.objectTypeMenu.open(event.currentTarget);
        }
    };

    render() {

        const { classes, fieldDef, refTypes } = this.props;

        const scope = this.props.scope || [];
        const limit = fieldDef.quantity === 'single' ? 1 : (Number.isInteger(fieldDef.quantity) && fieldDef.quantity);
        const fullyOccupied  = limit && this.props.fieldItems.length >= limit;
        const selectionConstraint = parseBSON(fieldDef.refconstraint, undefined, fieldDef.id);
        const selectionFlags = {
            replace: fullyOccupied && limit === 1,
            preference: fieldDef.preference || "selection",
            scope: scope
        };

        const standardHandlers = {
            onSelect: (index, object) => this.props.pushAndEditObject(object.id, this.props.readOnly),
            onEdit: (index, object) => this.props.pushAndEditObject(object.id, this.props.readOnly),
            onRemove: (index, object) => this.props.removeFieldElementAction(this.props.objectId, [...scope, fieldDef.id], index),
            onSearchTag: this.props.searchTag
        };
        const addHandlers = refTypes.length > 0 && !fullyOccupied ? {
            onAdd: this.handleAddReference(fieldDef.id, refTypes, selectionConstraint, selectionFlags),
        } : {};
        const replaceHandlers = refTypes.length > 0 && fullyOccupied && limit === 1 ? {
            onReplace: this.handleAddReference(fieldDef.id, refTypes, selectionConstraint, selectionFlags)
        } : {};
        const listHandlers = limit !== 1 ? {
            onMove: (fromIndex, toIndex) => this.props.moveFieldElementAction(this.props.objectId, [...scope, fieldDef.id], fromIndex, toIndex)
        } : {};
        const handlers = {
            ...standardHandlers,
            ...addHandlers,
            ...replaceHandlers,
            ...listHandlers
        };
        const menuHandler = typeId => this.props.selectReference(
            [...scope, fieldDef.id],
            typeId,
            selectionConstraint,
            selectionFlags
        );

        return <div className={classes.reference}>
            <InputLabel className={classes.referenceLabel}>{fieldDef.name}</InputLabel>
            <ObjectTable
                items={this.props.fieldItems}
                readOnly={this.props.readOnly}
                id={fieldDef.id}
                label={fieldDef.name}
                {...handlers}
                asField={true}
                pagination={false}
            />
            <ObjectTypeMenu
                ref={component => {this.objectTypeMenu = component}}
                types={refTypes}
                onSelect={menuHandler}
            />
        </div>;
    }
}

ObjectReferenceField.propTypes = {
    onObjectValueChange: PropTypes.func.isRequired,
    fieldDef: PropTypes.object.isRequired,
    objectId: PropTypes.string,
    readOnly: PropTypes.bool,
    value: PropTypes.any
};

const styles = {
    reference: {
        marginTop: 16,
        marginBottom: 8,
    },
    referenceLabel: {
        marginBottom: 8,
        display: "inline-block",
        transform: "translate(8px, 17.5px) scale(0.75)",
        transformOrigin: "top left",
        padding: "0 6px",
        backgroundColor: "white"
    }
};

const mapStateToProps = (state, props) => {
    return {
        refTypes: selectDerivedTypes(state.types, props.fieldDef.reftypes || []),
        fieldItems: selectObjectsForIds(state.editing, props.value),
    };
};

export default connect(mapStateToProps, {
    pushAndEditObject, selectReference, removeFieldElementAction, moveFieldElementAction,
    searchTag, openMediaOverlayForObject
})(withStyles(styles)(ObjectReferenceField));